import { useState, useEffect, useCallback } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { SignatureCanvas } from "@/components/logistica/SignatureCanvas";
import { gerarFL02PDF } from "@/lib/fl02PDF";
import { FileSignature, Plus, Printer, Undo2, ClipboardCheck } from "lucide-react";

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

// 4.3 — Cautela individual FL-02
export default function CautelasPage() {
  const [cautelas, setCautelas] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [obras, setObras] = useState([]);
  const [sel, setSel] = useState(null);
  const [dlg, setDlg] = useState(null); // abrir | retirar | devolver:{item} | conferencia
  const [form, setForm] = useState({});

  const carregar = useCallback(async () => {
    const r = await axios.get(`${API}/logistica/cautelas`).catch(() => null);
    if (r) setCautelas(r.data);
  }, []);
  const abrirDetalhe = useCallback(async (id) => {
    const r = await axios.get(`${API}/logistica/cautelas/${id}`).catch(() => null);
    if (r) setSel(r.data);
  }, []);
  useEffect(() => {
    carregar();
    axios.get(`${API}/funcionarios`).then(r => setFuncionarios(r.data)).catch(() => {});
    axios.get(`${API}/cronogramas-lookup/contratos-disponiveis`).then(r => setObras(r.data)).catch(() => {});
    const abrir = new URLSearchParams(window.location.search).get("abrir");
    if (abrir) abrirDetalhe(abrir);
  }, [carregar, abrirDetalhe]);

  const [patrimonios, setPatrimonios] = useState([]);
  useEffect(() => {
    if (dlg === "retirar") axios.get(`${API}/logistica/busca-itens?q=`).then(r =>
      setPatrimonios(r.data.filter(x => x.tipo === "patrimonio" && !x.bloqueio))).catch(() => {});
  }, [dlg]);

  const acao = async (fn, ok) => {
    try { const r = await fn(); toast.success(r.data.message || ok); setDlg(null); setForm({});
      carregar(); if (sel) abrirDetalhe(sel.id); }
    catch (err) { toast.error(extractErrorMessage(err, "Erro na operação")); }
  };

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="cautelas-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-luvcorp-navy flex items-center gap-2">
              <FileSignature className="h-6 w-6 text-luvcorp-gold" /> Cautelas (FL-02)
            </h1>
            <p className="text-sm text-slate-500">Ficha individual por colaborador — termo assinado uma vez na abertura (Regimento, Arts. 10º a 14º).</p>
          </div>
          <Button onClick={() => { setForm({}); setDlg("abrir"); }} className="bg-luvcorp-navy text-white" data-testid="btn-abrir-cautela">
            <Plus className="h-4 w-4 mr-1" /> Abrir cautela
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {cautelas.length === 0 ? <p className="p-8 text-center text-slate-400 text-sm">Nenhuma ficha aberta.</p> :
                cautelas.map(c => (
                  <button key={c.id} onClick={() => abrirDetalhe(c.id)} data-testid={`cautela-row-${c.funcionario_nome?.split(" ")[0]}`}
                    className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${sel?.id === c.id ? "bg-slate-50" : ""}`}>
                    <p className="font-semibold text-sm text-luvcorp-navy">{c.funcionario_nome} <span className="text-xs text-slate-400">· folha {c.folha}</span></p>
                    <p className="text-xs text-slate-500">{c.em_posse} item(ns) em posse · {c.total_itens} no histórico</p>
                  </button>
                ))}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-4">
              {!sel ? <p className="p-8 text-center text-slate-400 text-sm">Selecione uma ficha.</p> : (
                <div className="space-y-3" data-testid="cautela-detalhe">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-bold text-luvcorp-navy">{sel.funcionario_nome} — FL-02 folha {sel.folha}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setForm({}); setDlg("retirar"); }} data-testid="btn-retirar-item"><Plus className="h-4 w-4 mr-1" /> Retirada</Button>
                      <Button size="sm" variant="outline" onClick={() => { setForm({ tudo_presente: true }); setDlg("conferencia"); }} data-testid="btn-conferencia-mensal"><ClipboardCheck className="h-4 w-4 mr-1" /> Conferência mensal</Button>
                      <Button size="sm" variant="outline" onClick={() => gerarFL02PDF(sel)} data-testid="btn-pdf-fl02"><Printer className="h-4 w-4 mr-1" /> PDF FL-02</Button>
                    </div>
                  </div>
                  <p className="text-[11px] italic text-slate-500 bg-slate-50 rounded p-2">"{sel.termo}" — assinado em {fmtD(sel.aberta_em)}</p>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500 border-b">
                      <th className="py-1.5">Retirada</th><th>Item</th><th>Obra</th><th>Devolução</th><th>Estado</th><th></th></tr></thead>
                    <tbody>
                      {(sel.itens || []).map(it => (
                        <tr key={it.id} className="border-b border-slate-100" data-testid={`cautela-item-${it.patrimonio_codigo}`}>
                          <td className="py-1.5">{fmtD(it.data_retirada)}</td>
                          <td>{it.patrimonio_nome} <span className="text-slate-400">{it.patrimonio_codigo}</span></td>
                          <td>{it.obra_titulo || "—"}</td>
                          <td>{it.data_devolucao ? fmtD(it.data_devolucao) : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">em posse</Badge>}</td>
                          <td>{it.estado_devolucao ? <Badge variant="outline" className={["D", "EXT"].includes(it.estado_devolucao) ? "bg-red-50 text-red-700 border-red-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}>{it.estado_devolucao}</Badge> : "—"}</td>
                          <td className="text-right">{it.status === "em_posse" && (
                            <Button size="sm" variant="ghost" onClick={() => { setForm({ estado: "B" }); setDlg({ devolver: it }); }} data-testid={`btn-devolver-${it.patrimonio_codigo}`}>
                              <Undo2 className="h-4 w-4 text-luvcorp-navy" /></Button>)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(sel.conferencias || []).length > 0 && (
                    <div className="text-xs text-slate-500">
                      <p className="font-semibold text-luvcorp-navy mb-1">Conferências mensais</p>
                      {sel.conferencias.map((c, i) => <p key={i}>{c.mes}: {c.tudo_presente ? "Tudo presente" : `Faltas/danos — ${c.faltas_danos}`} ({c.registrada_por})</p>)}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Abrir cautela */}
      <Dialog open={dlg === "abrir"} onOpenChange={v => !v && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Abrir ficha de cautela</DialogTitle></DialogHeader>
          <NativeSelect value={form.funcionario_id || ""} onValueChange={v => setForm(f => ({ ...f, funcionario_id: v }))} searchable
            placeholder="Colaborador…" options={funcionarios.map(f => ({ value: f.id, label: f.nome }))} data-testid="select-colaborador-cautela" />
          <p className="text-xs italic text-slate-500">"Declaro que as ferramentas e equipamentos registrados abaixo estão sob minha responsabilidade… (Arts. 10º a 14º)". O colaborador assina UMA vez abaixo:</p>
          <SignatureCanvas onChange={v => setForm(f => ({ ...f, assinatura_base64: v }))} />
          <DialogFooter>
            <Button onClick={() => acao(() => axios.post(`${API}/logistica/cautelas`, form), "Cautela aberta")}
              disabled={!form.funcionario_id || !form.assinatura_base64} className="bg-luvcorp-navy text-white" data-testid="btn-confirmar-abertura">Abrir ficha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retirada */}
      <Dialog open={dlg === "retirar"} onOpenChange={v => !v && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Retirada de ferramenta/equipamento</DialogTitle></DialogHeader>
          <NativeSelect value={form.patrimonio_id || ""} onValueChange={v => setForm(f => ({ ...f, patrimonio_id: v }))}
            placeholder="Patrimônio disponível…" options={patrimonios.map(p => ({ value: p.ref_id, label: `${p.codigo} — ${p.nome}` }))} data-testid="select-patrimonio-cautela" />
          <NativeSelect value={form.contrato_id || ""} onValueChange={v => setForm(f => ({ ...f, contrato_id: v }))}
            placeholder="Obra (opcional)…" options={[{ value: "", label: "— Sem obra —" }, ...obras.map(o => ({ value: o.contrato_id, label: o.titulo }))]} data-testid="select-obra-cautela" />
          <p className="text-xs text-slate-500">Rubrica do colaborador:</p>
          <SignatureCanvas onChange={v => setForm(f => ({ ...f, assinatura_base64: v }))} />
          <DialogFooter>
            <Button onClick={() => acao(() => axios.post(`${API}/logistica/cautelas/${sel.id}/retirar`, form), "Retirada registrada")}
              disabled={!form.patrimonio_id || !form.assinatura_base64} className="bg-luvcorp-navy text-white" data-testid="btn-confirmar-retirada">Registrar retirada</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Devolução */}
      <Dialog open={!!dlg?.devolver} onOpenChange={v => !v && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Devolução — {dlg?.devolver?.patrimonio_nome}</DialogTitle></DialogHeader>
          <NativeSelect value={form.estado || "B"} onValueChange={v => setForm(f => ({ ...f, estado: v }))} data-testid="select-estado-devolucao"
            options={[{ value: "B", label: "B — Bom" }, { value: "R", label: "R — Regular" },
                      { value: "D", label: "D — Danificado (abre ocorrência FL-01)" }, { value: "EXT", label: "EXT — Extraviado (abre ocorrência + apuração)" }]} />
          <Input placeholder="Observação (opcional)" value={form.observacao || ""} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} data-testid="input-obs-devolucao" />
          <p className="text-xs text-slate-500">Rubrica da Logística (opcional):</p>
          <SignatureCanvas onChange={v => setForm(f => ({ ...f, assinatura_logistica: v }))} />
          <DialogFooter>
            <Button onClick={() => acao(() => axios.post(`${API}/logistica/cautelas/${sel.id}/devolver/${dlg.devolver.id}`, form), "Devolução registrada")}
              className="bg-luvcorp-navy text-white" data-testid="btn-confirmar-devolucao">Registrar devolução</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferência mensal */}
      <Dialog open={dlg === "conferencia"} onOpenChange={v => !v && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Conferência mensal — {sel?.funcionario_nome}</DialogTitle></DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.tudo_presente !== false} onCheckedChange={v => setForm(f => ({ ...f, tudo_presente: !!v }))} data-testid="check-tudo-presente" />
            Tudo presente? (desmarque para informar faltas/danos)
          </label>
          {form.tudo_presente === false && (
            <Input placeholder="Faltas/danos (item e nº patrimônio) *" value={form.faltas_danos || ""}
              onChange={e => setForm(f => ({ ...f, faltas_danos: e.target.value }))} data-testid="input-faltas-danos" />)}
          <p className="text-xs text-slate-500">Assinatura da Logística / do colaborador (opcional):</p>
          <SignatureCanvas onChange={v => setForm(f => ({ ...f, assinatura_logistica_base64: v }))} />
          <DialogFooter>
            <Button onClick={() => acao(() => axios.post(`${API}/logistica/cautelas/${sel.id}/conferencia-mensal`,
              { tudo_presente: form.tudo_presente !== false, faltas_danos: form.faltas_danos, assinatura_logistica_base64: form.assinatura_logistica_base64 }), "Conferência registrada")}
              className="bg-luvcorp-navy text-white" data-testid="btn-confirmar-conferencia">Registrar conferência</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
